import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicDevis, signPublicDevis, getPublicSignedPdfUrl } from '../services/api';
import { CheckCircle, FileText, AlertCircle, Loader2, Upload, Pencil, Download, Mail } from 'lucide-react';

export default function PublicSignaturePage() {
  const { token } = useParams();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signataireName, setSignataireName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [signatureDate, setSignatureDate] = useState(() => {
    // Default to today (YYYY-MM-DD in local time)
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signedDevisId, setSignedDevisId] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'upload'
  const [uploadedImage, setUploadedImage] = useState(null);
  
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    loadDevis();
  }, [token]);

  const loadDevis = async () => {
    try {
      setLoading(true);
      const response = await getPublicDevis(token);
      setDevis(response.data);
      // Pre-fill email if provided by admin
      if (response.data.client_email) {
        setClientEmail(response.data.client_email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setUploadedImage(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setHasSignature(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSign = async () => {
    if (!signataireName.trim()) {
      alert('Veuillez entrer votre nom');
      return;
    }
    
    let signatureImage = '';
    
    if (signatureMode === 'draw') {
      if (!hasSignature) {
        alert('Veuillez dessiner votre signature');
        return;
      }
      const canvas = canvasRef.current;
      signatureImage = canvas.toDataURL('image/png');
    } else {
      if (!uploadedImage) {
        alert('Veuillez importer votre tampon ou signature');
        return;
      }
      signatureImage = uploadedImage;
    }

    setSigning(true);
    try {
      const response = await signPublicDevis(token, {
        signature_image: signatureImage,
        signataire_nom: signataireName.trim(),
        client_email: clientEmail.trim() || null,
        date_signature: signatureDate || null
      });
      
      // Store the devis_id for PDF download
      if (response.data?.devis_id) {
        setSignedDevisId(response.data.devis_id);
      }
      if (response.data?.email_sent) {
        setEmailSent(true);
      }
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Lien invalide</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Devis signé !</h1>
          <p className="text-slate-600 mb-6">
            Merci {signataireName}, votre signature a été enregistrée avec succès.
          </p>
          
          {/* Email confirmation */}
          {emailSent && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-center gap-2 text-blue-700">
                <Mail className="w-5 h-5" />
                <span className="text-sm font-medium">Le PDF signé a été envoyé à votre email</span>
              </div>
            </div>
          )}
          
          {/* Download PDF button */}
          {signedDevisId && (
            <a
              href={getPublicSignedPdfUrl(signedDevisId)}
              download
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors mb-4"
            >
              <Download className="w-5 h-5" />
              Télécharger le devis signé (PDF)
            </a>
          )}
          
          <p className="text-sm text-slate-500 mt-4">
            Vous pouvez fermer cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-xl font-bold text-white">Signature de devis</h1>
                <p className="text-blue-100 text-sm">MAAG - Aménagement de magasins</p>
              </div>
            </div>
          </div>
          
          {/* Devis info */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Projet</p>
                <p className="text-slate-800 font-semibold">{devis.projet_nom}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Client</p>
                <p className="text-slate-800 font-semibold">{devis.client}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-slate-500 uppercase font-medium">Objet du devis</p>
                <p className="text-slate-800 font-semibold">{devis.objet}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-slate-500 uppercase font-medium">Adresse du chantier</p>
                <p className="text-slate-700">{devis.adresse}</p>
              </div>
            </div>
            
            {/* Totals */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-600">Total HT</span>
                <span className="font-semibold text-slate-800">{formatMoney(devis.total_ht)}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-600">TVA (20%)</span>
                <span className="font-semibold text-slate-800">{formatMoney(devis.total_ht * 0.20)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-slate-800">Total TTC</span>
                  <span className="text-lg font-bold text-blue-600">{formatMoney(devis.total_ttc)}</span>
                </div>
              </div>
            </div>
            
            {/* Signature form */}
            <div className="border-t border-slate-200 pt-6">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Votre signature</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nom du signataire *
                </label>
                <input
                  type="text"
                  value={signataireName}
                  onChange={(e) => setSignataireName(e.target.value)}
                  placeholder="Votre nom complet"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Votre email (optionnel)
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="exemple@email.com"
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Renseignez votre email pour recevoir le PDF signé automatiquement
                </p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Date de signature *
                </label>
                <input
                  type="date"
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                  data-testid="signature-date-input"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Par défaut aujourd'hui — modifiable si vous souhaitez une autre date de signature.
                </p>
              </div>
              
              {/* Signature mode selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mode de signature *
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setSignatureMode('draw'); setUploadedImage(null); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-colors ${
                      signatureMode === 'draw' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Pencil className="w-5 h-5" />
                    Dessiner
                  </button>
                  <button
                    onClick={() => { setSignatureMode('upload'); clearSignature(); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-colors ${
                      signatureMode === 'upload' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Upload className="w-5 h-5" />
                    Importer tampon
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                {signatureMode === 'draw' ? (
                  <>
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                      <canvas
                        ref={canvasRef}
                        width={500}
                        height={150}
                        className="w-full border border-slate-200 rounded cursor-crosshair bg-white touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <button
                      onClick={clearSignature}
                      className="mt-2 text-sm text-slate-500 hover:text-slate-700 underline"
                    >
                      Effacer la signature
                    </button>
                  </>
                ) : (
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-white text-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    {uploadedImage ? (
                      <div>
                        <img 
                          src={uploadedImage} 
                          alt="Tampon/Signature" 
                          className="max-h-32 mx-auto mb-2 border border-slate-200 rounded"
                        />
                        <button
                          onClick={() => { setUploadedImage(null); setHasSignature(false); }}
                          className="text-sm text-red-500 hover:text-red-700 underline"
                        >
                          Supprimer
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex flex-col items-center gap-2 py-4 w-full text-slate-500 hover:text-slate-700"
                      >
                        <Upload className="w-10 h-10" />
                        <span>Cliquez pour importer votre tampon ou signature</span>
                        <span className="text-xs text-slate-400">PNG, JPG, GIF acceptés</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              <p className="text-xs text-slate-500 mb-4">
                En signant ce devis, vous acceptez les conditions générales de vente et vous engagez à régler les montants indiqués selon les modalités convenues.
              </p>
              
              <button
                onClick={handleSign}
                disabled={signing || !signataireName.trim() || !hasSignature || !signatureDate}
                className="w-full py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signature en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Signer le devis
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <p className="text-center text-sm text-slate-500">
          MAAG - Aménagement de magasins © 2026
        </p>
      </div>
    </div>
  );
}
